import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Tab,
  Table,
  Tabs,
  Typography,
  styled,
} from '@material-ui/core';
import { ParagraphText } from '../Markdown/ParagraphText';

// --- Design tokens ---
const FONT = { display: 'Red Hat Display', body: 'Open Sans' } as const;
const FONT_SIZE = {
  body: 14,
  bodyLarge: 16,
  toc: 12,
  title: 22,
  h1: 40,
} as const;
const LINE_HEIGHT = { body: '22px', toc: '18px', title: '30px' } as const;
const bodyText = {
  fontFamily: FONT.body,
  fontSize: FONT_SIZE.body,
  lineHeight: LINE_HEIGHT.body,
} as const;

// --- Layout ---

export const PageContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: 24,
  marginBottom: 24,
});

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: FONT.display,
  fontWeight: 700,
  fontSize: 34,
  color: theme.palette.text.primary,
  marginBottom: 20,
}));

export const TabsWrapper = styled('div')({
  marginBottom: 24,
});

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 44,
  '& .MuiTabs-indicator': {
    height: 2,
    backgroundColor: theme.palette.primary.main,
  },
}));

export const StyledTab = styled(Tab)(({ theme }) => ({
  minWidth: 0,
  padding: '10px 30px',
  fontFamily: FONT.display,
  fontSize: FONT_SIZE.body,
  fontWeight: 700,
  lineHeight: '16px',
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  ['&:not(&.Mui-selected)']: {
    color: theme.palette.text.secondary,
  },
}));

export const ContentGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 320px)',
  gap: 24,
  alignItems: 'start',
});

export const DocsContent = styled('div')({
  minWidth: 0,
});

export const TabSectionsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

export const TableOfContentsWrapper = styled('aside')({
  position: 'sticky',
  top: 24,
  width: '100%',
});

// --- Typography ---

export const IntroText = styled(Typography)(({ theme }) => ({
  ...bodyText,
  color: theme.palette.text.primary,
  marginBottom: 26,
}));

export const SectionTitle = styled(Typography)({
  fontFamily: FONT.display,
  fontWeight: 700,
  marginBottom: 8,
});

export const SectionSubtitle = styled(Typography)(({ theme }) => ({
  ...bodyText,
  color: theme.palette.text.secondary,
  marginBottom: 16,
}));

// --- Lists ---

export const BulletList = styled('ul')(({ theme }) => ({
  paddingLeft: 16,
  '& li::marker': { color: theme.palette.primary.main },
}));

export const BulletItem = styled('li')({
  ...bodyText,
  marginBottom: 12,
});

export const NavLinkText = styled(RouterLink)(({ theme }) => ({
  fontFamily: FONT.body,
  color: theme.palette.primary.main,
  fontWeight: 700,
  textDecoration: 'none',
}));

export const DocContentLink = styled(RouterLink)(({ theme }) => ({
  fontFamily: FONT.body,
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: 16,
  color: theme.palette.primary.main,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

// --- Section rows ---

export const SectionRow = styled('div')({ marginBottom: 20 });

export const SectionRowHeading = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: FONT.display,
  fontSize: FONT_SIZE.body,
  fontWeight: 700,
  marginBottom: 8,
});

export const SectionIconWrapper = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minWidth: 24,
  color: theme.palette.primary.main,
}));

export const SectionRowDescription = styled(Typography)(({ theme }) => ({
  ...bodyText,
  color: theme.palette.text.primary,
}));

export const NumberList = styled('ol')({
  margin: '0 0 24px 0',
  paddingLeft: 20,
});

export const NumberItem = styled('li')({
  ...bodyText,
  marginBottom: 12,
});

// --- Callout ---

export const CalloutBox = styled('div')(({ theme }) => ({
  ...bodyText,
  border: `1px solid ${theme.palette.primary.light}`,
  backgroundColor: '#F0F4FF',
  borderRadius: 6,
  padding: '12px 14px',
  color: theme.palette.text.primary,
}));

// --- Table of contents ---

export const TocContainer = styled('div')({ paddingTop: 2 });

export const TocTitle = styled(Typography)({
  fontFamily: FONT.display,
  fontWeight: 700,
  fontSize: FONT_SIZE.title,
  lineHeight: LINE_HEIGHT.title,
  marginBottom: 22,
});

export const TocGroupTitle = styled(Typography)(({ theme }) => ({
  fontFamily: FONT.display,
  fontWeight: 500,
  fontSize: FONT_SIZE.toc,
  lineHeight: LINE_HEIGHT.toc,
  color: theme.palette.text.primary,
  marginBottom: 16,
}));

export const TocActiveGroupTitle = styled(TocGroupTitle)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
}));

export const TocGroup = styled('div')({
  marginBottom: 18,
});

export const TocItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontFamily: FONT.body,
  fontSize: FONT_SIZE.toc,
  lineHeight: LINE_HEIGHT.toc,
  color: theme.palette.text.primary,
  marginBottom: 12,
}));

export const TocActiveItem = styled(TocItem)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
}));

export const TocIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  fontSize: 21,
  color: theme.palette.primary.main,
}));

export const TocLink = styled(RouterLink)({
  color: 'inherit',
  fontWeight: 'inherit',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});

// --- Getting Started / Info cards ---

export const GettingStartedTitle = styled(Typography)({
  fontFamily: FONT.display,
  fontWeight: 700,
  marginBottom: 12,
});

export const GettingStartedIntro = styled(Typography)(({ theme }) => ({
  ...bodyText,
  color: theme.palette.text.primary,
  marginBottom: 20,
}));

export const InfoCard = styled('section')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 10,
  backgroundColor: theme.palette.background.paper,
  padding: '22px 22px 10px',
  marginBottom: 22,
  '&[data-variant="transparent"]': {
    backgroundColor: 'transparent',
  },
}));

export const InfoCardTitle = styled(Typography)({
  fontFamily: FONT.display,
  fontWeight: 700,
  marginBottom: 12,
});

export const InfoCardDescription = styled(Typography)(({ theme }) => ({
  ...bodyText,
  color: theme.palette.text.primary,
  marginBottom: 12,
}));

export const InfoCardList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: 18,
  '& li': { ...bodyText, marginBottom: 12 },
  '& li::marker': { color: theme.palette.primary.main },
}));

// --- Landing Page Guide ---

export { ParagraphText };

export const LandingNumberList = styled('ol')(({ theme }) => ({
  margin: '0 0 24px 0',
  paddingLeft: 24,
  '& li': {
    ...bodyText,
    marginBottom: 12,
    color: theme.palette.text.primary,
  },
  '& li::marker': {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

export const FeatureBlock = styled('div')({ marginBottom: 14 });

export const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontFamily: FONT.display,
  fontSize: FONT_SIZE.body,
  lineHeight: LINE_HEIGHT.body,
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: 2,
}));

// --- Help card ---

export const HelpCard = styled('section')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 10,
  backgroundColor: theme.palette.background.paper,
  padding: '18px 20px',
  marginTop: 8,
}));

export const HelpCardTitle = styled(Typography)({
  fontFamily: FONT.display,
  fontWeight: 700,
  marginBottom: 12,
});

export const HelpQuestionList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: 20,
  '& li': { marginBottom: 16 },
  '& li::marker': { color: theme.palette.primary.main },
  '& li > span': {
    fontFamily: FONT.body,
    fontSize: FONT_SIZE.bodyLarge,
    fontWeight: 'normal',
    color: theme.palette.primary.main,
    marginBottom: 16,
  },
}));

// --- Release Notes ---

export const ReleaseNotesList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

export const ReleaseNotesListItem = styled('div')({
  '& span': {
    fontFamily: FONT.body,
    fontSize: FONT_SIZE.bodyLarge,
    fontWeight: 'normal',
  },
});

// --- Markdown doc ---

export const ArticleContentWrapper = styled('div')({
  maxWidth: 1200,
});

export const BackButton = styled(Button)({
  fontWeight: 700,
  marginBottom: 16,
});

export const MarkdownDocTitle = styled('h1')(({ theme }) => ({
  fontFamily: FONT.display,
  fontWeight: 700,
  fontSize: FONT_SIZE.h1,
  color: theme.palette.text.primary,
  margin: '0 0 8px 0',
}));

export const MarkdownDocParagraph = styled(Typography)(({ theme }) => ({
  fontFamily: FONT.body,
  fontSize: FONT_SIZE.bodyLarge,
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  marginBottom: 16,
}));

export const MarkdownDocTable = styled(Table)(({ theme }) => ({
  fontFamily: FONT.body,
  fontSize: FONT_SIZE.bodyLarge,
  marginBottom: 24,
  '& .MuiTableCell-root': {
    border: `1px solid ${theme.palette.divider}`,
    padding: '12px 16px',
  },
  '& .MuiTableCell-head': {
    fontWeight: 700,
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const MarkdownInlineCode = styled('code')(({ theme }) => ({
  fontFamily:
    'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 14,
  lineHeight: '20px',
  borderRadius: 3,
  padding: '2px 6px',
  color: theme.palette.text.primary,
  backgroundColor: '#EEF1F6',
  border: `1px solid ${theme.palette.divider}`,
  display: 'inline',
  verticalAlign: 'baseline',
}));

export const MarkdownCodeBlockContainer = styled('div')(({ theme }) => ({
  marginBottom: 24,
  borderRadius: 6,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  backgroundColor: '#F7F8FA',
}));

export const MarkdownCodeHeader = styled('div')(({ theme }) => ({
  height: 30,
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  padding: '0 10px',
}));

export const MarkdownCodeCopyIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  lineHeight: 0,
}));

export const MarkdownCodeBody = styled('div')({
  overflowX: 'auto',
  padding: '10px 0',
});

export const MarkdownCodeLine = styled('div')({
  display: 'grid',
  gridTemplateColumns: '36px 1fr',
  alignItems: 'start',
  gap: 8,
  minHeight: 24,
  padding: '0 12px',
});

export const MarkdownCodeLineNumber = styled('span')(({ theme }) => ({
  fontFamily:
    'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 14,
  lineHeight: '24px',
  color: theme.palette.text.secondary,
  userSelect: 'none',
  textAlign: 'right',
}));

export const MarkdownCodeLineText = styled('code')({
  fontFamily:
    'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 14,
  lineHeight: '24px',
  color: '#2F4F34',
  whiteSpace: 'pre',
});

export const MarkdownAdmonition = styled('section')(({ theme }) => ({
  margin: '0 0 20px 0',
  borderRadius: 6,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  backgroundColor: '#F9FAFC',
  '&[data-variant="warning"]': {
    borderColor: '#D6A33B',
  },
  '&[data-variant="tip"]': {
    borderColor: '#33B8A9',
  },
  '&[data-variant="danger"]': {
    borderColor: '#D23F65',
  },
  '&[data-variant="info"]': {
    borderColor: '#3CB6D0',
  },
}));

export const MarkdownAdmonitionHeader = styled('div')({
  minHeight: 42,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '0 14px',
  '&[data-variant="warning"]': {
    backgroundColor: '#E8E3DF',
  },
  '&[data-variant="tip"]': {
    backgroundColor: '#D0DEE5',
  },
  '&[data-variant="danger"]': {
    backgroundColor: '#EBD7E0',
  },
  '&[data-variant="info"]': {
    backgroundColor: '#D0DEE5',
  },
});

export const MarkdownAdmonitionIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 0,
  '&[data-variant="warning"]': {
    color: '#EE9B00',
  },
  '&[data-variant="tip"]': {
    color: '#0CA678',
  },
  '&[data-variant="danger"]': {
    color: '#E11D48',
  },
  '&[data-variant="info"]': {
    color: '#0891B2',
  },
});

export const MarkdownAdmonitionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: FONT.display,
  fontWeight: 700,
  fontSize: FONT_SIZE.bodyLarge,
  color: theme.palette.text.primary,
}));

export const MarkdownAdmonitionBody = styled('div')({
  padding: '14px',
  '& > :last-child': {
    marginBottom: 0,
  },
});
